/**
 * Video URL parsing utilities for various video platforms
 */

export interface ParsedVideoData {
  platform: "youtube" | "vimeo" | "direct" | "hls" | "embedded" | "unknown";
  videoId?: string;
  playlistId?: string;
  originalUrl: string;
  embedUrl?: string;
  thumbnailUrl?: string;
  isPlaylist: boolean;
}

export function isYouTubeUrl(url: string): boolean {
  return url?.includes("youtube.com") || url?.includes("youtu.be");
}

export function getYouTubeVideoId(url: string): string | null {
  return parseYouTubeUrl(url).videoId ?? null;
}

/**
 * Parse YouTube URL and extract video/playlist information
 */
export function parseYouTubeUrl(url: string): ParsedVideoData {
  const result: ParsedVideoData = {
    platform: "youtube",
    originalUrl: url,
    isPlaylist: false,
  };

  // YouTube video ID patterns
  const videoPatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
  ];

  // YouTube playlist pattern
  const playlistMatch = url.match(/[?&]list=([^&\n?#]+)/);
  if (playlistMatch) {
    result.playlistId = playlistMatch[1];
    result.isPlaylist = true;
  }

  // Extract video ID
  for (const pattern of videoPatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      result.videoId = match[1];
      break;
    }
  }

  // Generate embed URL and thumbnail
  if (result.videoId) {
    let embedUrl = `https://www.youtube.com/embed/${result.videoId}`;
    const params = new URLSearchParams();

    // Add playlist if available
    if (result.playlistId) {
      params.append("list", result.playlistId);
    }

    // Default embed parameters for signage
    params.append("autoplay", "1");
    params.append("mute", "1");
    params.append("controls", "0");
    params.append("showinfo", "0");
    params.append("rel", "0");
    params.append("modestbranding", "1");

    if (params.toString()) {
      embedUrl += `?${params.toString()}`;
    }

    result.embedUrl = embedUrl;
    result.thumbnailUrl = `https://img.youtube.com/vi/${result.videoId}/maxresdefault.jpg`;
  }

  return result;
}

/**
 * Parse Vimeo URL and extract video information
 */
export function parseVimeoUrl(url: string): ParsedVideoData {
  const result: ParsedVideoData = {
    platform: "vimeo",
    originalUrl: url,
    isPlaylist: false,
  };

  // Vimeo video ID pattern
  const match = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
  if (match && match[1]) {
    result.videoId = match[1];
    result.embedUrl = `https://player.vimeo.com/video/${result.videoId}?autoplay=1&muted=1&controls=0`;
  }

  return result;
}

/**
 * Check if URL is a direct video file
 */
export function isDirectVideoUrl(url: string): boolean {
  const videoExtensions = [
    ".mp4",
    ".webm",
    ".ogg",
    ".mov",
    ".avi",
    ".mkv",
    ".m4v",
    ".m3u8",
  ];
  const urlLower = url.toLowerCase();
  return videoExtensions.some((ext) => urlLower.includes(ext));
}

export function isHlsUrl(url: string): boolean {
  return url.toLowerCase().includes(".m3u8");
}

/**
 * Main function to parse any video URL
 */
export function parseVideoUrl(url: string): ParsedVideoData {
  if (!url) {
    return {
      platform: "unknown",
      originalUrl: url,
      isPlaylist: false,
    };
  }

  // YouTube
  if (isYouTubeUrl(url)) {
    return parseYouTubeUrl(url);
  }

  // Vimeo
  if (url.includes("vimeo.com")) {
    return parseVimeoUrl(url);
  }

  // HLS playlists
  if (isHlsUrl(url)) {
    return {
      platform: "hls",
      originalUrl: url,
      isPlaylist: false,
      embedUrl: url,
    };
  }

  // Direct video file
  if (isDirectVideoUrl(url)) {
    return {
      platform: "direct",
      originalUrl: url,
      isPlaylist: false,
      embedUrl: url,
    };
  }

  // Generic web pages can be embedded in an iframe if the provider allows it.
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
      return {
        platform: "embedded",
        originalUrl: url,
        isPlaylist: false,
        embedUrl: url,
      };
    }
  } catch {
    // Fall through to unknown.
  }

  // Unknown/embed
  return {
    platform: "unknown",
    originalUrl: url,
    isPlaylist: false,
  };
}

/**
 * Generate video thumbnail URL based on platform
 */
export function getVideoThumbnail(parsedVideo: ParsedVideoData): string | null {
  switch (parsedVideo.platform) {
    case "youtube":
      return parsedVideo.videoId
        ? `https://img.youtube.com/vi/${parsedVideo.videoId}/maxresdefault.jpg`
        : null;

    case "vimeo":
      // Vimeo requires API call for thumbnails, return null for now
      return null;

    default:
      return null;
  }
}

/**
 * Validate if a URL is a supported video format
 */
export function isSupportedVideoUrl(url: string): boolean {
  const parsed = parseVideoUrl(url);
  return parsed.platform !== "unknown" || isDirectVideoUrl(url);
}

/**
 * Extract duration from YouTube URL parameters (if provided)
 */
export function extractYouTubeDuration(url: string): number | null {
  const match = url.match(/[?&]t=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Generate optimized embed parameters for digital signage
 */
export function generateSignageEmbedParams(): URLSearchParams {
  const params = new URLSearchParams();

  // Autoplay and audio
  params.append("autoplay", "1");

  // Hide UI elements
  params.append("controls", "0");
  params.append("showinfo", "0");
  params.append("rel", "0");
  params.append("modestbranding", "1");

  // Quality and performance
  params.append("enablejsapi", "1");
  params.append("origin", window.location.origin);

  return params;
}

/**
 * Create embed URL with signage-optimized parameters
 */
export function createSignageEmbedUrl(
  parsedVideo: ParsedVideoData,
): string | null {
  if (!parsedVideo.embedUrl) return null;

  const [baseUrl, existingParams] = parsedVideo.embedUrl.split("?");
  const signageParams = generateSignageEmbedParams();

  // Merge existing params with signage params
  if (existingParams) {
    const existing = new URLSearchParams(existingParams);
    signageParams.forEach((value, key) => {
      existing.set(key, value); // Override with signage params
    });
    return `${baseUrl}?${existing.toString()}`;
  }

  return `${baseUrl}?${signageParams.toString()}`;
}
